import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  WaterDrop,
  CheckCircle,
  Lightbulb,
  Warning,
  Info,
} from '@mui/icons-material';
import SupportShell from './SupportShell';

const WaterQualityManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState<string | false>('water-testing');

  const handleBackToSupport = () => {
    navigate('/support');
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const waterQualityParameters = [
    { parameter: 'pH', optimal: '5.5 - 6.5', critical: '< 5.0 or > 7.5', impact: 'Nutrient availability' },
    { parameter: 'EC (mS/cm)', optimal: '1.2 - 2.4', critical: '> 3.0', impact: 'Osmotic stress' },
    { parameter: 'Alkalinity (ppm CaCO₃)', optimal: '< 100', critical: '> 200', impact: 'pH buffering' },
    { parameter: 'Calcium (ppm)', optimal: '150 - 300', critical: '> 500', impact: 'Nutrient balance' },
    { parameter: 'Magnesium (ppm)', optimal: '50 - 100', critical: '> 150', impact: 'Chlorophyll synthesis' },
    { parameter: 'Sulfur (ppm)', optimal: '50 - 150', critical: '> 300', impact: 'Protein synthesis' },
    { parameter: 'Sodium (ppm)', optimal: '< 50', critical: '> 100', impact: 'Salt toxicity' },
    { parameter: 'Chloride (ppm)', optimal: '< 75', critical: '> 150', impact: 'Leaf burn' },
  ];

  const sections = [
    {
      id: 'water-testing',
      title: 'Water Testing & Analysis',
      icon: <WaterDrop color="primary" />,
      content: {
        overview: 'Comprehensive water analysis is the foundation of successful hydroponic nutrition.',
        keyPoints: [
          'Essential water quality parameters',
          'Testing frequency and methodology',
          'Interpreting test results',
          'Quality control and calibration',
        ],
        details: `Water quality testing should include pH, EC, alkalinity, and individual ion concentrations. Use calibrated meters and follow proper sampling procedures. Test source water monthly and nutrient solutions weekly.

Key parameters include pH (5.5-6.5 optimal), EC (1.2-2.4 mS/cm for most crops), alkalinity (<100 ppm CaCO₃), and individual ions like Ca, Mg, S, Na, and Cl. High alkalinity water requires more acid for pH adjustment and provides pH buffering.`,
        tips: [
          'Calibrate pH and EC meters before each use',
          'Take samples from multiple points in the system',
          'Test at consistent times for trend analysis',
          'Keep detailed records for pattern recognition',
        ],
      },
    },
    {
      id: 'filtration-systems',
      title: 'Filtration & Treatment Systems',
      icon: <WaterDrop color="primary" />,
      content: {
        overview: 'Selecting and maintaining appropriate water treatment systems for hydroponic applications.',
        keyPoints: [
          'Reverse osmosis (RO) systems',
          'Carbon filtration for chlorine removal',
          'UV sterilization for pathogen control',
          'System maintenance and monitoring',
        ],
        details: `RO systems remove 95-99% of dissolved minerals, providing a clean slate for nutrient formulation. Carbon filters remove chlorine and chloramines that can damage beneficial microorganisms. UV sterilization eliminates pathogens without chemical residues.

System selection depends on source water quality and crop requirements. High TDS water (>300 ppm) typically requires RO treatment, while moderate quality water may only need carbon filtration.`,
        tips: [
          'Size RO systems for peak demand plus 20% capacity',
          'Replace carbon filters every 6-12 months',
          'Monitor RO membrane performance with TDS meters',
          'Install pressure gauges to monitor system health',
        ],
      },
    },
    {
      id: 'mineral-balance',
      title: 'Mineral Balance & Interactions',
      icon: <WaterDrop color="primary" />,
      content: {
        overview: 'Understanding mineral interactions and maintaining optimal nutrient ratios.',
        keyPoints: [
          'Cation-anion balance principles',
          'Antagonistic nutrient interactions',
          'Synergistic nutrient relationships',
          'Seasonal adjustment strategies',
        ],
        details: `Mineral balance affects nutrient uptake efficiency and plant health. High calcium levels can reduce magnesium and potassium uptake. Excessive sodium interferes with potassium absorption. Sulfate and chloride compete for uptake sites.

The cation-anion balance should be maintained within acceptable ranges. Calculate the difference between total cations and anions in milliequivalents to assess balance. Adjust formulations to maintain proper ratios.`,
        tips: [
          'Monitor Ca:Mg ratios (aim for 3:1 to 5:1)',
          'Keep sodium below 50 ppm for sensitive crops',
          'Balance sulfate and chloride levels',
          'Adjust ratios based on growth stage and season',
        ],
      },
    },
    {
      id: 'quality-standards',
      title: 'Quality Standards & Monitoring',
      icon: <WaterDrop color="primary" />,
      content: {
        overview: 'Establishing and maintaining water quality standards for consistent production.',
        keyPoints: [
          'Industry quality standards',
          'Crop-specific requirements',
          'Monitoring protocols',
          'Corrective action procedures',
        ],
        details: `Establish quality standards based on crop requirements and industry guidelines. Document acceptable ranges for all parameters and implement monitoring protocols. Create standard operating procedures for corrective actions when parameters exceed limits.

Different crops have varying tolerance levels. Leafy greens are generally more tolerant of higher EC levels, while fruiting crops may require more precise control. Adjust standards based on specific crop requirements and growth stages.`,
        tips: [
          'Create quality control checklists',
          'Train staff on proper testing procedures',
          'Implement automated monitoring where possible',
          'Document all corrective actions taken',
        ],
      },
    },
  ];

    return (
    <SupportShell
      title="Water quality"
      subtitle="Source water analysis and how background ions enter the formulation."
    >

          {/* Introduction Card */}
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Foundation of Hydroponic Success
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                Water quality is the cornerstone of successful hydroponic cultivation. Poor water quality can 
                lead to nutrient imbalances, plant stress, reduced yields, and system failures. Understanding 
                water chemistry and implementing proper treatment protocols ensures optimal growing conditions 
                and consistent production results.
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                This comprehensive guide covers water testing, treatment systems, mineral balance, and quality 
                monitoring protocols used by professional hydroponic operations worldwide.
              </Typography>
            </CardContent>
          </Card>

          {/* Water Quality Parameters Table */}
          <Paper sx={{ mb: 4 }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" component="h3" sx={{ fontWeight: 600, mb: 1.5 }}>
                Critical Water Quality Parameters
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Parameter</strong></TableCell>
                      <TableCell><strong>Optimal Range</strong></TableCell>
                      <TableCell><strong>Critical Level</strong></TableCell>
                      <TableCell><strong>Primary Impact</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {waterQualityParameters.map((param, index) => (
                      <TableRow key={index}>
                        <TableCell>{param.parameter}</TableCell>
                        <TableCell>
                          <Chip label={param.optimal} color="success" variant="outlined" size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip label={param.critical} color="error" variant="outlined" size="small" />
                        </TableCell>
                        <TableCell>{param.impact}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>

          {/* Learning Objectives */}
          <Paper sx={{ p: 4, mb: 4, bgcolor: 'info.50' }}>
            <Typography variant="h5" component="h3" sx={{ fontWeight: 600, mb: 1.5 }}>
              Learning Objectives
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Master water testing procedures and parameter interpretation" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Select and maintain appropriate filtration systems" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Understand mineral interactions and balance requirements" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Implement quality monitoring and control protocols" />
              </ListItem>
            </List>
          </Paper>

          {/* Content Sections */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 4 }}>
            Core Topics
          </Typography>

          {sections.map((section) => (
            <Accordion
              key={section.id}
              expanded={expandedSection === section.id}
              onChange={handleAccordionChange(section.id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ 
                  '& .MuiAccordionSummary-content': { 
                    alignItems: 'center',
                    gap: 2 
                  }
                }}
              >
                {section.icon}
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                  {section.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 4 }}>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                  {section.content.overview}
                </Typography>

                <Typography variant="h6" component="h4" sx={{ fontWeight: 600, mb: 2 }}>
                  Key Concepts
                </Typography>
                <List sx={{ mb: 3 }}>
                  {section.content.keyPoints.map((point, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={point} />
                    </ListItem>
                  ))}
                </List>

                <Typography variant="h6" component="h4" sx={{ fontWeight: 600, mb: 2 }}>
                  Detailed Explanation
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                  {section.content.details}
                </Typography>

                <Paper sx={{ p: 3, bgcolor: 'warning.50' }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Lightbulb color="warning" />
                    <Box>
                      <Typography variant="h6" component="h5" sx={{ fontWeight: 600, mb: 2 }}>
                        Professional Tips
                      </Typography>
                      <List>
                        {section.content.tips.map((tip, index) => (
                          <ListItem key={index} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <Info color="info" sx={{ fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText primary={tip} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Stack>
                </Paper>
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Next Steps */}
          <Card variant="outlined" sx={{ mt: 3 }}>
            <CardContent sx={{ p: 2, textAlign: 'left' }}>
              <Typography variant="h5" component="h3" sx={{ fontWeight: 600, mb: 1.5 }}>
                Ready to Implement Water Quality Management?
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.7 }}>
                Apply your water quality knowledge using FlahaFast's professional water analysis tools 
                and nutrient calculation system.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={() => navigate('/support')}
                  sx={{ borderRadius: 2 }}
                >
                  All guides
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/login')}
                  sx={{ borderRadius: 2 }}
                >
                  Open app
                </Button>
              </Stack>
            </CardContent>
          </Card>
    </SupportShell>
  );
};

export default WaterQualityManagementPage;
